import z from "zod";

// ============================================================================
// Guest Schemas
// ============================================================================

const BaseGuestPropsSchema = z.object({
  allowed_origins: z.array(z.string()).optional(),
});

export const WhatsappGuestSchema = BaseGuestPropsSchema.extend({
  type: z.literal("whatsapp"),
  mode: z.enum(["chat", "voice"]).default("chat"),
  phone_number: z.string(),
  contact_name: z.string(),
});

export const LivekitGuestSchema = BaseGuestPropsSchema.extend({
  type: z.literal("livekit"),
  room_name: z.string(),
  bot_id: z.string().optional(),
  agent_id: z.string().optional(),
  environment: z.enum(["sip", "webrtc"]),
  phone_number: z.string().optional(),
  hostname: z.string().nullish(),
});

export const ChatWidgetGuestSchema = BaseGuestPropsSchema.extend({
  type: z.literal("chat-widget"),
  agent_id: z.string(),
  origin: z.string(),
});

export const TelegramGuestSchema = BaseGuestPropsSchema.extend({
  type: z.literal("telegram"),
  chat_id: z.string(),
  user_id: z.string(),
  first_name: z.string(),
});

/**
 * Discriminated union of all guest types
 * Allows type narrowing based on the 'type' field
 */
export const GuestSchema = z.discriminatedUnion("type", [
  WhatsappGuestSchema,
  LivekitGuestSchema,
  ChatWidgetGuestSchema,
  TelegramGuestSchema,
]);

// ============================================================================
// TypeScript Types (Inferred from Zod schemas)
// ============================================================================

export type WhatsappGuest = z.infer<typeof WhatsappGuestSchema>;
export type LivekitGuest = z.infer<typeof LivekitGuestSchema>;
export type ChatWidgetGuest = z.infer<typeof ChatWidgetGuestSchema>;
export type TelegramGuest = z.infer<typeof TelegramGuestSchema>;
export type Guest = z.infer<typeof GuestSchema>;

// ============================================================================
// Usage Request Schemas
// ============================================================================

export const UsageRequestItemSchema = z.object({
  id: z.string(),
  charge_type: z.string().optional(),
  charge_subtype: z.string(),
  identifier: z.string().optional(),
  display_identifier: z.string().nullish(),
  purchased_by_user_id: z.string().nullish(),
  pricing_rule: z.string(),
  unit: z.string(),
  quantity: z.number(),
  price_amount: z.number(),
  execution_time_ms: z.number().optional(),
  cost_attribution: z.enum(["platform", "client_byok"]).default("platform"),
  // LLM input_token items only. null/absent = provider did not report; 0 is a
  // real zero. `quantity` is the whole prompt, cache included.
  cache_read_tokens: z.number().nullish(),
  cache_write_tokens: z.number().nullish(),
  // client_byok input_token only: what the provider charged the org's own key.
  // Information only, never part of the platform cost.
  provider_reported_cost_usd: z.number().nullish(),
  // Performance metrics
  throughput: z.number().nullish(),
  ttfb_ms: z.number().nullish(),
  is_cached: z.boolean().nullish(),
  performance_details: z.record(z.string(), z.any()).nullish(),
});

export type UsageRequestItem = z.infer<typeof UsageRequestItemSchema>;

export const UsageRequestSchema = z.object({
  agent_id: z.string().nullish(),
  api_key_id: z.string().nullish(),
  auth_mode: z.string(),
  id: z.string(),
  user_id: z.string().nullish(),
  project_id: z.string().nullish(),
  bot_id: z.string().nullish(),
  thread_id: z.string().nullish(),
  room_name: z.string().nullish(),
  source_service: z.string().nullish(),
  // Performance metrics
  processing_time_ms: z.number().nullish(),
  execution_time_ms: z.number().nullish(),
  first_response_ms: z.number().nullish(),
  total_duration_ms: z.number().nullish(),
  speed: z.number().nullish(),
  // Delivery and metadata
  delivery_method: z.enum(["http", "streaming", "batch", "realtime", "webhook"]).nullish(),
  performance_metadata: z.record(z.string(), z.any()).nullish(),
  // Timestamps
  event_timestamp: z.string(),
  items: z.array(UsageRequestItemSchema).nullish(),
  guest: GuestSchema.nullish(),
  total_price_local: z.number(),
});

export type UsageRequest = z.infer<typeof UsageRequestSchema>;
